import { useState, useMemo } from "react";
import { useKV } from "@/hooks/use-kv-adapter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus, Minus, CaretDown, CaretRight } from "@phosphor-icons/react";

interface OrgNodeProps {
  node: any;
  level: number;
  onExpand: (id: string) => void;
  expanded: Set<string>;
}

function OrgNode({ node, level, onExpand, expanded }: OrgNodeProps) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expanded.has(node.id);

  return (
    <div className="flex flex-col items-center">
      <Card className="w-64 border-l-4 border-l-blue-500 mb-4 hover:shadow-lg transition-all">
        <CardContent className="p-4 flex items-center gap-3">
          <Avatar>
            <AvatarImage src={node.avatar} />
            <AvatarFallback>{node.name?.[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1 overflow-hidden">
            <h4 className="font-semibold truncate">{node.name}</h4>
            <p className="text-xs text-slate-500 truncate">{node.role}</p>
            <Badge variant="outline" className="mt-1 text-[10px]">{node.department}</Badge>
          </div>
        </CardContent>
      </Card>
      
      {hasChildren && (
        <div className="flex flex-col items-center">
          <div className="h-4 w-px bg-slate-300 dark:bg-slate-700"></div>
          <Button 
            variant="outline" 
            size="sm" 
            className="rounded-full h-6 w-6 p-0 mb-4 z-10 bg-white dark:bg-slate-900"
            onClick={() => onExpand(node.id)}
          >
            {isExpanded ? <Minus className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
          </Button>
          
          {isExpanded && (
            <div className="flex gap-8 relative">
              {/* Horizontal connector line */}
              {node.children.length > 1 && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[calc(100%-16rem)] h-px bg-slate-300 dark:bg-slate-700 -translate-y-4"></div>
              )}
              
              {node.children.map((child: any) => (
                <div key={child.id} className="relative pt-4">
                  {/* Vertical connector to child */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 h-4 w-px bg-slate-300 dark:bg-slate-700 -translate-y-4"></div>
                  <OrgNode 
                    node={child} 
                    level={level + 1} 
                    onExpand={onExpand} 
                    expanded={expanded} 
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function OrgChart() {
  const [users] = useKV<any[]>("employees", []);
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['root']));

  // Build hierarchy tree from flat user list
  const tree = useMemo(() => {
    // Find CEO/Head (no manager or role=superadmin)
    // For demo, if no clear head, pick the first superadmin or first user
    if (users.length === 0) return null;
    
    // This is a simplified tree builder assuming 'managerId' or similar relationship
    // Since our User type doesn't explicitly have managerId yet in KV, we'll mock a structure or use departments
    // TODO: Implement proper manager relationship in User type
    
    // Mock tree for visualization if no real hierarchy exists
    return {
      id: 'root',
      name: 'CEO / Founder',
      role: 'Chief Executive Officer',
      department: 'Executive',
      avatar: '',
      children: users.slice(0, 3).map((u, i) => ({
        ...u,
        children: users.slice(3 + (i * 2), 3 + (i * 2) + 2)
      }))
    };
  }, [users]);

  const handleExpand = (id: string) => {
    const newExpanded = new Set(expanded);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpanded(newExpanded);
  };

  if (!tree) return <div>Loading Org Chart...</div>;

  return (
    <div className="overflow-auto p-8 flex justify-center min-h-[500px]">
      <OrgNode 
        node={tree} 
        level={0} 
        onExpand={handleExpand} 
        expanded={expanded} 
      />
    </div>
  );
}
